import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import IconButton from '@mui/material/IconButton';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Tooltip from '@mui/material/Tooltip';

import { Button, TextField, Card, Select } from '../components/ui-kit';
import { ConfirmationDialog, AvatarWithFallback } from '../components';
import {
  getUserById,
  updateUser,
  assignRoleToUser,
  removeRoleFromUser,
  uploadAvatar,
} from '../api/users';
import { getRoles } from '../api/roles';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import { getAvatarUrl } from '../utils/avatarHelpers';

// Validation schema
const validationSchema = Yup.object({
  fullName: Yup.string().required('Full name is required'),
  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),
});

function UserEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission('auth-service', 'user', 'update');
  const isOwnProfile = currentUser?.id === id;
  const canAssignRoles = hasPermission('auth-service', 'role', 'assign');
  const canUnassignRoles = hasPermission('auth-service', 'role', 'unassign');
  const canManageRoles = canAssignRoles || canUnassignRoles;
  const [selectedRoles, setSelectedRoles] = useState([]);
  
  // Helper function to check if a role is super-admin
  const isSuperAdminRole = (roleName) => roleName === 'super-admin';
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [nextLocation, setNextLocation] = useState(null);

  // Get user data
  const {
    data: userData,
    isLoading: isLoadingUser,
    isError: isErrorUser,
    error: userError,
  } = useQuery(['user', id], () => getUserById(id), {
    onSuccess: (data) => {
      // Set user roles if they exist
      if (data.user.roles && data.user.roles.length > 0) {
        setSelectedRoles(data.user.roles.map((role) => role._id || role.id));
      }
    },
  });

  // Get roles list
  const { data: rolesData, isInitialLoading: isLoadingRoles } = useQuery(
    ['roles'],
    getRoles,
    {
      enabled: canManageRoles,
    }
  );

  // Mutation for updating user data
  const updateUserMutation = useMutation(
    (userData) => updateUser(id, userData),
    {
      onSuccess: () => {
        toast.success('User data updated successfully');
        queryClient.invalidateQueries(['user', id]);
        formik.resetForm({ values: formik.values });
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Error updating user');
      },
    }
  );

  // Mutation for assigning a role (role:assign)
  const assignRoleMutation = useMutation((roleId) => assignRoleToUser(id, roleId), {
    onSuccess: () => {
      toast.success('Role assigned successfully');
      queryClient.invalidateQueries(['user', id]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error assigning role');
      queryClient.invalidateQueries(['user', id]);
    },
  });

  // Mutation for removing a role (role:unassign)
  const unassignRoleMutation = useMutation((roleId) => removeRoleFromUser(id, roleId), {
    onSuccess: () => {
      toast.success('Role removed successfully');
      queryClient.invalidateQueries(['user', id]);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error removing role');
      queryClient.invalidateQueries(['user', id]);
    },
  });

  // Mutation for uploading avatar
  const uploadAvatarMutation = useMutation((file) => uploadAvatar(id, file), {
    onSuccess: () => {
      toast.success('Avatar uploaded successfully');
      queryClient.invalidateQueries(['user', id]);
      setIsUploading(false);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Error uploading avatar');
      setIsUploading(false);
    },
  });

  // Initialize form
  const formik = useFormik({
    initialValues: {
      fullName: userData?.user.fullName || '',
      email: userData?.user.email || '',
      avatar: userData?.user.avatar || '',
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: (values) => {
      updateUserMutation.mutate(values);
    },
  });

  // Unsaved changes confirmation
  useEffect(() => {
    // Handler for navigation blocking
    const handleBeforeUnload = (event) => {
      if (formik.dirty && !updateUserMutation.isSuccess) {
        const message =
          'You have unsaved changes. Are you sure you want to leave?';
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [formik.dirty, updateUserMutation.isSuccess]);

  // Handle navigation with unsaved changes
  const handleNavigationWithUnsavedChanges = (to) => {
    if (formik.dirty && !updateUserMutation.isSuccess) {
      setNextLocation(to);
      setConfirmDialogOpen(true);
    } else {
      navigate(to);
    }
  };

  // Confirm navigation and discard changes
  const confirmNavigation = () => {
    setConfirmDialogOpen(false);
    if (nextLocation) {
      navigate(nextLocation);
    }
  };

  // Roles change handler
  const handleRolesChange = (event) => {
    const newRoles = event.target.value;
    const attemptedAdd = newRoles.filter((roleId) => !selectedRoles.includes(roleId));
    const attemptedRemove = selectedRoles.filter((roleId) => !newRoles.includes(roleId));

    if (attemptedAdd.length > 0 && !canAssignRoles) {
      toast.error('You do not have permission to assign roles');
    }
    if (attemptedRemove.length > 0 && !canUnassignRoles) {
      toast.error('You do not have permission to remove roles');
    }

    const added = canAssignRoles ? attemptedAdd : [];
    const removed = canUnassignRoles ? attemptedRemove : [];

    // Only update state for permitted changes
    const nextRoles = [
      ...selectedRoles.filter((id) => !removed.includes(id)),
      ...added,
    ];
    setSelectedRoles(nextRoles);
    added.forEach((roleId) => assignRoleMutation.mutate(roleId));
    removed.forEach((roleId) => unassignRoleMutation.mutate(roleId));
  };

  // Back to user list handler
  const handleBack = () => {
    handleNavigationWithUnsavedChanges('/users');
  };

  // Avatar button click handler
  const handleAvatarButtonClick = () => {
    fileInputRef.current.click();
  };

  // Avatar file change handler
  const handleAvatarChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type and size
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
    const maxSize = 2 * 1024 * 1024; // 2MB

    if (!allowedTypes.includes(file.type)) {
      toast.error(
        'Unsupported file format. Only JPEG, PNG and GIF are supported'
      );
      return;
    }

    if (file.size > maxSize) {
      toast.error('File size should not exceed 2MB');
      return;
    }

    setIsUploading(true);
    uploadAvatarMutation.mutate(file);
  };

  if (isLoadingUser || isLoadingRoles) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isErrorUser) {
    return (
      <Box>
        <Alert severity="error">Error loading data: {userError.message}</Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          sx={{ mt: 2 }}
          onClick={handleBack}
          aria-label="Return to users list"
        >
          Back to list
        </Button>
      </Box>
    );
  }

  const { user } = userData;

  // Transform roles for Select component
  const roleOptions =
    rolesData && rolesData.roles
      ? rolesData.roles.map((role) => ({
          value: role._id || role.id,
          label: role.name,
        }))
      : [];

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: '100%',
      }}
    >
      {/* Page Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          aria-label="Back to users list"
        >
          Back
        </Button>
        <Typography variant="h4" component="h1" fontWeight="500">
          Edit User
        </Typography>
      </Box>

      <Grid container spacing={3}>
        {/* User Information */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              borderRadius: '12px',
              background: (theme) =>
                `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.background.default} 100%)`,
              boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              position: 'relative',
              transition: 'all 0.3s ease',
            }}
          >
            <Box sx={{ position: 'relative', mb: 3 }}>
              <AvatarWithFallback
                src={getAvatarUrl(user.avatar)}
                alt={user.fullName}
                size={110}
                sx={{
                  backgroundColor: 'action.selected',
                  color: 'text.secondary',
                  border: (theme) =>
                    `4px solid ${theme.palette.background.paper}`,
                  boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                }}
              />
              {isUploading ? (
                <CircularProgress
                  size={30}
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    backgroundColor: 'background.paper',
                    borderRadius: '50%',
                    padding: '2px',
                    boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  }}
                />
              ) : (
                <Tooltip title="Change avatar" arrow>
                  <span>
                    <IconButton
                      sx={{
                        position: 'absolute',
                        bottom: 0,
                        right: 0,
                        backgroundColor: (theme) =>
                          theme.palette.background.paper,
                        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                        transition: 'all 0.2s',
                        '&:hover': {
                          backgroundColor: (theme) => theme.palette.primary.light,
                          color: (theme) => theme.palette.primary.contrastText,
                        },
                      }}
                      onClick={handleAvatarButtonClick}
                      disabled={!canEdit && !isOwnProfile}
                      aria-label="Change user avatar"
                    >
                      <PhotoCameraIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
              <input
                type="file"
                accept="image/jpeg, image/jpg, image/png, image/gif"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleAvatarChange}
                aria-label="Upload avatar file"
              />
            </Box>

            <Box
              sx={{
                width: '100%',
                p: 2,
                borderRadius: '8px',
                backgroundColor: 'action.hover',
              }}
            >
              {/* Name Row */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  alignItems: 'center',
                }}
              >
                <span>Name:</span>
                <Tooltip title={user.fullName} arrow placement="top">
                  <span
                    style={{
                      fontWeight: 600,
                      maxWidth: '65%',
                      textAlign: 'right',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: (theme) => theme.palette.text.primary,
                      cursor: 'pointer',
                    }}
                  >
                    {user.fullName}
                  </span>
                </Tooltip>
              </Typography>

              {/* Email Row */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  alignItems: 'flex-start',
                }}
              >
                <span>Email:</span>
                <Tooltip title={user.email} arrow placement="top">
                  <span
                    style={{
                      fontWeight: 500,
                      maxWidth: '65%',
                      textAlign: 'right',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      cursor: 'pointer',
                    }}
                  >
                    {user.email}
                  </span>
                </Tooltip>
              </Typography>

              {/* User ID Row */}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  alignItems: 'center',
                }}
              >
                <span>ID:</span>
                <Tooltip title={user.id} arrow placement="top">
                  <span
                    style={{
                      fontWeight: 500,
                      maxWidth: '65%',
                      textAlign: 'right',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                    }}
                  >
                    {user.id}
                  </span>
                </Tooltip>
              </Typography>

              {/* Status Row */}
              <Typography
                component="div"
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  alignItems: 'center',
                }}
              >
                <span>Status:</span>
                <Chip
                  label={user.isActive ? 'Active' : 'Banned'}
                  color={user.isActive ? 'success' : 'error'}
                  size="small"
                  sx={{
                    fontWeight: 500,
                    fontSize: '0.75rem',
                    height: '20px',
                  }}
                />
              </Typography>

              {/* Email Verified Row */}
              <Typography
                component="div"
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  alignItems: 'center',
                }}
              >
                <span>Email:</span>
                <Tooltip
                  title={
                    user.isEmailVerified
                      ? 'Email confirmed'
                      : 'Email not confirmed — local password login is blocked until verified'
                  }
                  arrow
                  placement="top"
                >
                  <Chip
                    label={user.isEmailVerified ? 'Verified' : 'Not verified'}
                    color={user.isEmailVerified ? 'success' : 'warning'}
                    variant={user.isEmailVerified ? 'filled' : 'outlined'}
                    size="small"
                    sx={{
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      height: '20px',
                    }}
                  />
                </Tooltip>
              </Typography>

              {/* Roles Row */}
              <Typography
                component="div"
                variant="body2"
                color="text.secondary"
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mb: 1.5,
                  alignItems: 'flex-start',
                }}
              >
                <span style={{ marginTop: '2px' }}>Roles:</span>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 0.5,
                    flexWrap: 'wrap',
                    justifyContent: 'flex-end',
                    maxWidth: '65%',
                  }}
                >
                  {user.roles && user.roles.length > 0 ? (
                    user.roles.map((role) => (
                      <Chip
                        key={role.id || role.name}
                        label={role.name}
                        size="small"
                        sx={{
                          bgcolor: (theme) =>
                            isSuperAdminRole(role.name)
                              ? 'primary.dark'
                              : 'action.selected',
                          color: 'text.primary',
                          fontWeight: 500,
                          height: '20px',
                          fontSize: '0.7rem',
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    ))
                  ) : (
                    <span style={{ fontWeight: 500, fontSize: '0.75rem' }}>
                      No roles
                    </span>
                  )}
                </Box>
              </Typography>

              {/* Created Date Row */}
              {user.metadata && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <span>Created:</span>
                  <Tooltip
                    title={new Date(user.metadata.createdAt).toLocaleString()}
                    arrow
                    placement="top"
                  >
                    <span
                      style={{
                        fontWeight: 500,
                        maxWidth: '65%',
                        textAlign: 'right',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        cursor: 'pointer',
                      }}
                    >
                      {new Date(user.metadata.createdAt).toLocaleDateString()}
                    </span>
                  </Tooltip>
                </Typography>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Edit Form */}
        <Grid item xs={12} md={8}>
          <Card
            sx={{
              p: 3,
              borderRadius: '12px',
              background: (theme) =>
                `linear-gradient(145deg, ${theme.palette.background.paper} 15%, ${theme.palette.background.default} 100%)`,
              boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
              overflow: 'hidden',
              position: 'relative',
              transition: 'all 0.3s ease',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                mb: 3,
                fontWeight: 600,
                position: 'relative',
                '&:after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  left: 0,
                  width: 40,
                  height: 3,
                  borderRadius: '3px',
                  bgcolor: 'primary.main',
                  backgroundImage: (theme) =>
                    `linear-gradient(to right, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                },
              }}
            >
              Basic Information
            </Typography>

            <Box component="form" onSubmit={formik.handleSubmit} noValidate>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="fullName"
                    name="fullName"
                    label="Full Name"
                    value={formik.values.fullName}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!canEdit}
                    error={
                      formik.touched.fullName && Boolean(formik.errors.fullName)
                    }
                    helperText={
                      formik.touched.fullName && formik.errors.fullName
                    }
                    aria-describedby="fullName-helper-text"
                    inputProps={{
                      'aria-label': 'Full name',
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    label="Email"
                    value={formik.values.email}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!canEdit}
                    error={formik.touched.email && Boolean(formik.errors.email)}
                    helperText={formik.touched.email && formik.errors.email}
                    aria-describedby="email-helper-text"
                    inputProps={{
                      'aria-label': 'Email address',
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="avatar"
                    name="avatar"
                    label="Avatar URL"
                    value={formik.values.avatar}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    disabled={!canEdit}
                    aria-describedby="avatar-helper-text"
                    inputProps={{
                      'aria-label': 'Avatar URL',
                    }}
                    InputProps={{
                      endAdornment: (
                        <Button
                          variant="contained"
                          component="label"
                          startIcon={<CloudUploadIcon />}
                          onClick={handleAvatarButtonClick}
                          disabled={!canEdit && !isOwnProfile}
                          aria-label="Upload avatar"
                        >
                          Upload
                        </Button>
                      ),
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={updateUserMutation.isLoading || !formik.dirty || !canEdit}
                    aria-label="Save changes"
                  >
                    {updateUserMutation.isLoading ? (
                      <CircularProgress size={24} />
                    ) : (
                      'Save'
                    )}
                  </Button>
                  {formik.dirty && (
                    <Typography
                      variant="caption"
                      color="primary"
                      sx={{
                        ml: 2,
                        display: 'inline-flex',
                        alignItems: 'center',
                      }}
                    >
                      You have unsaved changes
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Box>

                          {canManageRoles && (
              <>
                <Divider
                  sx={{
                    my: 3,
                    opacity: 0.6,
                    borderColor: 'divider',
                  }}
                />

                <Typography
                  variant="h6"
                  sx={{
                    mb: 3,
                    fontWeight: 600,
                    position: 'relative',
                    '&:after': {
                      content: '""',
                      position: 'absolute',
                      bottom: -8,
                      left: 0,
                      width: 40,
                      height: 3,
                      borderRadius: '3px',
                      bgcolor: 'secondary.main',
                      backgroundImage: (theme) =>
                        `linear-gradient(to right, ${theme.palette.secondary.main}, ${theme.palette.secondary.light})`,
                    },
                  }}
                >
                  Role Management
                </Typography>

                <Select
                  fullWidth
                  multiple
                  label="Roles"
                  value={selectedRoles}
                  onChange={handleRolesChange}
                  disabled={assignRoleMutation.isLoading || unassignRoleMutation.isLoading}
                  options={roleOptions}
                  aria-label="Select user roles"
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((roleId) => {
                        const roleName = roleOptions.find((o) => o.value === roleId)?.label || roleId;
                        return (
                          <Chip
                            key={roleId}
                            label={roleName}
                            sx={{
                              bgcolor: (theme) =>
                                isSuperAdminRole(roleName)
                                  ? 'primary.dark'
                                  : 'action.selected',
                              color: 'text.primary',
                            }}
                          />
                        );
                      })}
                    </Box>
                  )}
                  sx={{ mb: 2 }}
                />
              </>
            )}
          </Card>
        </Grid>
      </Grid>

      {/* Unsaved Changes Dialog using ConfirmationDialog component */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={confirmNavigation}
        title="Unsaved Changes"
        contentText="You have unsaved changes. Are you sure you want to leave this page? All unsaved changes will be lost."
        confirmButtonText="Discard Changes"
        confirmButtonColor="error"
        cancelButtonText="Stay"
      />
    </Box>
  );
}

export default UserEdit;
